// Lead Sidebar Component

import { useState, useMemo } from 'react';
import { useMapStore, useSearchStore } from '../../stores';
import type { Business, LeadFilter } from '../../types';
import { LeadCard } from './LeadCard';
import { FilterPanel } from './FilterPanel';
import { 
  cn, 
  formatDistance, 
  getLeadTypeLabel, 
  getLeadTypeColor,
  getCategoryIcon,
  sortLeads,
  filterLeads 
} from '../../lib/utils';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  ChevronUp,
  MapPin,
  Star,
  MessageCircle,
  Globe,
  AlertTriangle,
  Target,
  Download,
  List,
  Grid
} from 'lucide-react';

interface LeadSidebarProps {
  onLeadClick: (lead: Business) => void;
  onClose?: () => void;
}

export function LeadSidebar({ onLeadClick, onClose }: LeadSidebarProps) {
  const {
    leads,
    selectedLead,
    hoveredLeadId,
    filters,
    setFilters,
    resetFilters,
    isLoadingLeads,
    sidebarOpen,
    viewMode,
    setViewMode,
  } = useMapStore();
  
  const { searchQuery, setSearchQuery, searchHistory } = useSearchStore();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'leadScore' | 'name' | 'rating' | 'reviews' | 'distance'>('leadScore');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Filter and sort leads
  const filteredLeads = useMemo(() => {
    let result = filterLeads(leads, filters);
    result = sortLeads(result, sortBy, sortOrder);
    return result;
  }, [leads, filters, sortBy, sortOrder]);

  // Get unique categories from filtered leads
  const categories = useMemo(() => {
    const cats = new Map<string, number>();
    filteredLeads.forEach(lead => {
      cats.set(lead.category, (cats.get(lead.category) || 0) + 1);
    });
    return Array.from(cats.entries()).sort((a, b) => b[1] - a[1]);
  }, [filteredLeads]);

  // Get lead type counts
  const leadTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {
      'no-website': 0,
      'marketing': 0,
      'b2b': 0,
      'multiple': 0,
    };
    filteredLeads.forEach(lead => {
      counts[lead.leadType] = (counts[lead.leadType] || 0) + 1;
    });
    return counts;
  }, [filteredLeads]);

  const handleSortChange = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const isCategoryExpanded = (category: string) => expandedCategories[category] !== false;

  return (
    <aside 
      className={cn(
        // Fill the positioned wrapper supplied by the caller instead of being
        // `fixed` to the viewport. As a fixed element it sat at top:0 with
        // h-full and z-40, which painted over the z-30 header and made the
        // logo, hamburger menu and sidebar toggle unclickable.
        'absolute inset-0 bg-white/95 backdrop-blur-xl border-r border-slate-200/50 shadow-xl z-40 transition-transform duration-300 ease-in-out flex flex-col',
        sidebarOpen ? 'w-96 translate-x-0' : '-translate-x-full w-96'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200/50 sticky top-0 bg-white/95 backdrop-blur-xl z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Leads</h2>
            <p className="text-xs text-slate-500">{filteredLeads.length} of {leads.length} leads</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title={viewMode === 'list' ? 'Map View' : 'List View'}
          >
            {viewMode === 'list' ? <MapPin className="w-5 h-5" /> : <List className="w-5 h-5" />}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors lg:hidden"
              title="Close Sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-4 border-b border-slate-200/50 sticky top-16 bg-white/95 backdrop-blur-xl z-10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search businesses..."
            className="input-field pl-10"
          />
        </div>
        
        {/* Search History */}
        {searchQuery === '' && searchHistory.length > 0 && (
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            <p className="text-xs text-slate-500 px-1 mb-1">Recent searches</p>
            {searchHistory.slice(0, 5).map((query, i) => (
              <button
                key={i}
                onClick={() => setSearchQuery(query)}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-between"
              >
                <span className="truncate">{query}</span>
                <Search className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}

        {/* Filter Toggle */}
        <div className="flex items-center justify-between mt-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'btn-secondary text-sm gap-2',
              showFilters && 'bg-primary-50 text-primary-700 border-primary-200'
            )}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {(filters.categories.length > 0 || 
              filters.leadTypes.length < 4 || 
              filters.minLeadScore > 0 ||
              filters.hasWebsite !== null) && (
              <span className="badge badge-primary">{Object.values(filters).filter(v => Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== 0 && v !== '').length}</span>
            )}
          </button>
          
          <button
            onClick={resetFilters}
            className="btn-ghost text-sm text-slate-500 hover:text-slate-700"
            disabled={filters.categories.length === 0 && 
              filters.leadTypes.length === 4 && 
              filters.minLeadScore === 0 &&
              filters.hasWebsite === null}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <FilterPanel 
          filters={filters} 
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
          leadCounts={leadTypeCounts}
        />
      )}

      {/* Sort Options */}
      <div className="px-4 py-2 border-b border-slate-200/50 sticky top-16 bg-white/95 backdrop-blur-xl z-10">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 font-medium">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value as typeof sortBy)}
            className="flex-1 input-field text-sm py-2"
          >
            <option value="leadScore">Lead Score</option>
            <option value="name">Name (A-Z)</option>
            <option value="rating">Rating</option>
            <option value="reviews">Reviews</option>
            <option value="distance">Distance</option>
          </select>
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title={sortOrder === 'asc' ? 'Descending' : 'Ascending'}
          >
            {sortOrder === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Lead List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoadingLeads ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="lead-card animate-pulse">
              <div className="p-4 space-y-3">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                <div className="h-3 bg-slate-200 rounded w-1/3"></div>
              </div>
            </div>
          ))
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Search className="w-12 h-12 mb-4 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No leads found</p>
            <p className="text-sm text-slate-500 text-center px-4 mt-1">
              Try adjusting your filters or search in a different area
            </p>
            <button
              onClick={resetFilters}
              className="btn-primary mt-4"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <>
            {/* Lead Type Summary */}
            <div className="flex gap-2 flex-wrap">
              {[
                { type: 'no-website' as const, label: 'No Website', icon: Globe },
                { type: 'marketing' as const, label: 'Needs Marketing', icon: AlertTriangle },
                { type: 'b2b' as const, label: 'B2B Leads', icon: Target },
                { type: 'multiple' as const, label: 'Multiple', icon: AlertTriangle },
              ].map(({ type, label, icon: Icon }) => (
                leadTypeCounts[type] > 0 && (
                  <button
                    key={type}
                    onClick={() => setFilters({ leadTypes: [type as 'no-website' | 'marketing' | 'b2b' | 'multiple'] })}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5',
                      filters.leadTypes.length === 1 && filters.leadTypes[0] === type
                        ? getLeadTypeColor(type).replace('bg-', 'bg-').replace('text-', 'text-')
                        : getLeadTypeColor(type).replace('bg-', 'bg-').replace('800', '100').replace('text-', 'text-') + ' hover:opacity-80'
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{label}</span>
                    <span className={cn('px-1.5 py-0.5 rounded-full text-xs', 
                      filters.leadTypes.length === 1 && filters.leadTypes[0] === type
                        ? 'bg-white/30 text-white'
                        : 'bg-white text-slate-600'
                    )}>{leadTypeCounts[type]}</span>
                  </button>
                )
              ))}
            </div>

            {/* Category Filters */}
            <div className="space-y-2">
              {categories.map(([category, count]) => (
                <div key={category} className="bg-white/50 rounded-lg border border-slate-200/50 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCategoryIcon(category)}</span>
                      <span className="font-medium text-slate-900 capitalize">{category}</span>
                      <span className="badge badge-primary">{count}</span>
                    </div>
                    <span className={cn('transition-transform', isCategoryExpanded(category) ? 'rotate-180' : '')}>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </span>
                  </button>
                  
                  {isCategoryExpanded(category) && (
                    <div className="px-3 pb-3 space-y-2 border-t border-slate-200/50">
                      {filteredLeads
                        .filter(l => l.category === category)
                        .slice(0, 10)
                        .map(lead => (
                          <LeadCard
                            key={lead.id}
                            lead={lead}
                            isSelected={selectedLead?.id === lead.id}
                            isHovered={hoveredLeadId === lead.id}
                            onClick={() => onLeadClick(lead)}
                            compact
                          />
                        ))}
                      {filteredLeads.filter(l => l.category === category).length > 10 && (
                        <button className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium py-2">
                          Show {filteredLeads.filter(l => l.category === category).length - 10} more...
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Export Button */}
        {filteredLeads.length > 0 && (
          <div className="pt-4 border-t border-slate-200/50">
            <button
              onClick={() => {
                // Export functionality would go here
              }}
              className="btn-secondary w-full gap-2"
              disabled={filteredLeads.length === 0}
            >
              <Download className="w-4 h-4" />
              <span>Export {filteredLeads.length} Leads (CSV)</span>
            </button>
          </div>
        )}
      </div>

      {/* Bottom Stats */}
      <div className="p-4 border-t border-slate-200/50 bg-white/50">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 rounded-lg bg-slate-50">
            <p className="text-2xl font-bold text-slate-900">{leads.length}</p>
            <p className="text-xs text-slate-500">Total Leads</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-50">
            <p className="text-2xl font-bold text-red-600">{leadTypeCounts['no-website']}</p>
            <p className="text-xs text-slate-500">No Website</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-50">
            <p className="text-2xl font-bold text-amber-600">{leadTypeCounts['marketing']}</p>
            <p className="text-xs text-slate-500">Need Marketing</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default LeadSidebar;